import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ExternalLink } from 'lucide-react';

export function About() {
  return (
    <div className="mx-auto max-w-4xl px-6 py-12">
      <div className="mb-8">
        <h1 className="text-4xl font-semibold tracking-tight">About</h1>
        <p className="mt-2 text-[var(--color-muted-foreground)]">
          How Heuristic Hero is built. The audience-facing methodology lives on the{' '}
          <a href="/learn" className="text-[var(--color-primary)] hover:underline">Learn</a>{' '}
          page; this is the implementation story.
        </p>
      </div>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Pipeline</CardTitle>
          <CardDescription>What happens between "Submit" and "Report ready"</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ol className="list-decimal space-y-2 pl-6">
            <li><strong>POST /api/jobs</strong> validates input (URL scheme, SSRF guard against private IPs, rate limit, idempotency key) and enqueues the job. Returns <code className="font-mono">202</code> with a <code className="font-mono">jobId</code>.</li>
            <li>An <strong>Azure Container Apps Job</strong> dequeues the message. KEDA scales replicas by queue depth — zero when idle.</li>
            <li>The worker drives <strong>headless Chromium</strong> via Playwright, captures above-fold + full-page screenshots and a trimmed DOM.</li>
            <li><strong>axe-core</strong> runs in the page context, returning WCAG-tagged violations.</li>
            <li>A single multimodal LLM call (text + images + DOM + axe JSON + your DESIGN.md) returns structured JSON: 10 NN/g sub-scores, accessibility interpretation, DESIGN.md alignment, pros/cons, cited evidence.</li>
            <li>Composite is computed (50/25/25 with DESIGN.md, 60/40 without). Percentile is computed against the corpus.</li>
            <li>Artifacts land in blob storage: <code className="font-mono">response.json</code>, <code className="font-mono">report.md</code>, <code className="font-mono">screenshots/*.png</code>, <code className="font-mono">axe.json</code>. A row appends to the evaluations table for percentile lookups.</li>
            <li>The SWA polls <strong>GET /api/jobs/{`{jobId}`}</strong> until status flips to <code className="font-mono">Completed</code>, then renders the Report.</li>
          </ol>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Microsoft Agent Framework + Declarative Agents</CardTitle>
          <CardDescription>Prompts live in YAML, not in C#.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Every LLM-using node is a <strong>Declarative Agent</strong> — its model, instructions,
            and parameters defined in <code className="font-mono">agents/heuristic-evaluator.yaml</code>.
            The C# worker loads the YAML at startup; editing a prompt and redeploying does not
            require a recompile, and the prompt history is auditable in git.
          </p>
          <p>
            Today the workflow is a single combined-pass agent. v0.2 splits it into three
            fan-out agents (NN/g, DESIGN.md alignment, accessibility) with a fan-in rollup, all
            wired through <code className="font-mono">WorkflowBuilder</code>.
          </p>
          <p>
            <a
              href="https://learn.microsoft.com/en-us/azure/ai-foundry/concepts/agents"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
            >
              <ExternalLink className="h-3 w-3" /> Microsoft Agent Framework on Microsoft Learn
            </a>
          </p>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Models — Microsoft Foundry</CardTitle>
          <CardDescription>Six vision-capable models, all in one Azure subscription.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Models are deployed once via Bicep and exposed to the SWA via{' '}
            <code className="font-mono">/api/models</code>. The dropdown in the submit form is data-
            driven — adding or removing a model is a one-line change in{' '}
            <code className="font-mono">infra/foundry.bicep</code> + an entry in the catalog.
          </p>
          <p>The current six:</p>
          <ul className="space-y-1">
            <li><code className="font-mono">gpt-5</code> — flagship, best quality.</li>
            <li><code className="font-mono">gpt-5-mini</code> — strong general-purpose.</li>
            <li><code className="font-mono">gpt-5-nano</code> — cheapest gpt-5; great for repeat runs.</li>
            <li><code className="font-mono">gpt-4o</code> — solid baseline with vision.</li>
            <li><code className="font-mono">gpt-4o-mini</code> — cheap baseline.</li>
            <li><code className="font-mono">o4-mini</code> — reasoning-tuned (slower, more deliberate).</li>
          </ul>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Architectural patterns</CardTitle>
          <CardDescription>Three documented Azure patterns + one HTTP error standard.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <ul className="space-y-3">
            <li>
              <a
                href="https://learn.microsoft.com/en-us/azure/architecture/patterns/queue-based-load-leveling"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[var(--color-primary)] hover:underline"
              >
                Queue-Based Load Leveling
              </a>
              <p className="mt-1 text-[var(--color-muted-foreground)]">
                Storage Queue absorbs job-submission spikes; the Container Apps Job drains at a
                steady rate. Prevents the slow Playwright + LLM workload from being coupled to
                HTTP request lifetimes.
              </p>
            </li>
            <li>
              <a
                href="https://learn.microsoft.com/en-us/azure/architecture/patterns/asynchronous-request-reply"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[var(--color-primary)] hover:underline"
              >
                Asynchronous Request-Reply
              </a>
              <p className="mt-1 text-[var(--color-muted-foreground)]">
                <code className="font-mono">202 Accepted</code> + <code className="font-mono">Location</code> header pointing
                at the status endpoint; the client polls every 2s. No long-lived HTTP connections,
                no blocked workers.
              </p>
            </li>
            <li>
              <a
                href="https://www.rfc-editor.org/rfc/rfc9457"
                target="_blank"
                rel="noreferrer"
                className="font-medium text-[var(--color-primary)] hover:underline"
              >
                RFC 9457 — Problem Details for HTTP APIs
              </a>
              <p className="mt-1 text-[var(--color-muted-foreground)]">
                All error responses use <code className="font-mono">application/problem+json</code> with{' '}
                <code className="font-mono">type</code>, <code className="font-mono">title</code>, <code className="font-mono">status</code>,{' '}
                <code className="font-mono">detail</code>, plus extensions like <code className="font-mono">traceId</code> and{' '}
                <code className="font-mono">retryAfterSeconds</code>.
              </p>
            </li>
          </ul>
        </CardContent>
      </Card>

      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Observability — OpenTelemetry</CardTitle>
          <CardDescription>Traces, metrics, and Gen AI semantics on day one.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3 text-sm">
          <p>
            Every code path emits OpenTelemetry spans + metrics. LLM calls follow the OTel{' '}
            <strong>Gen AI Semantic Conventions</strong> — <code className="font-mono">gen_ai.request.model</code>,{' '}
            <code className="font-mono">gen_ai.usage.input_tokens</code>,{' '}
            <code className="font-mono">gen_ai.usage.output_tokens</code>, etc. — which means dashboards
            and cost reporting are uniform across providers if we ever add another.
          </p>
          <p>
            Custom metrics under the meter <code className="font-mono">HeuristicHero</code>:
            jobs.submitted/queued/started/completed/failed (counters), playwright/axe/workflow
            duration (histograms), cost.usd (histogram), ratelimit.rejected,
            ssrf.rejected, spendcap.rejected.
          </p>
          <p>
            <a
              href="https://opentelemetry.io/docs/specs/semconv/gen-ai/"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
            >
              <ExternalLink className="h-3 w-3" /> OTel Gen AI Semantic Conventions
            </a>
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Tech stack</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {[
              '.NET 10', 'C#',
              'Azure Functions (isolated)', 'Azure Container Apps Jobs', 'Azure Static Web Apps',
              'Azure Storage (Blob + Queue + Tables)', 'Azure AI Services (Foundry)',
              'Microsoft Agent Framework', 'Microsoft.Extensions.AI',
              'Bicep (IaC)', 'GitHub Actions (OIDC)',
              'Playwright', 'axe-core',
              'React 19', 'Vite', 'TypeScript',
              'Tailwind CSS v4', 'shadcn/ui patterns',
              'Recharts', 'TanStack Query', 'react-router',
              'OpenTelemetry', 'Application Insights',
            ].map((tag) => (
              <Badge key={tag} variant="outline" className="font-mono text-xs">{tag}</Badge>
            ))}
          </div>
          <div className="mt-4 text-sm">
            <a
              href="https://github.com/CrankingAI/heuristic-hero"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"
            >
              <ExternalLink className="h-3 w-3" /> Source on GitHub
            </a>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
